import { Dispatch, createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axiosInstance from "../../utils/axios";
import { baseUrl } from "../../config";

export interface Address {
  street: string;
  postalCode: string;
  city: string;
}

export enum InvoiceDeliveryMethod {
  EMAIL = "EMAIL",
  POST = "POST",
  E_INVOICE = "E_INVOICE", // optional future
}

//export type InvoiceDeliveryMethod = "EMAIL" | ""

export interface User {
  id?: number;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  password: string;
  properties?: any;
  address?: Address;
  invoiceDeliveryMethod: InvoiceDeliveryMethod;
}

export interface UserState {
  user: User | undefined;
  users: User[];
  open: boolean;
  editable: boolean;
  status: "idle" | "loading" | "failed";
}

const initialState: UserState = {
  user: undefined,
  users: [],
  open: false,
  editable: false,
  status: "idle",
};

export const fetchUsers = createAsyncThunk(
  "users/fetchUsers",
  async (associationId: number) => {
    const response = await axiosInstance(
      `/users?associationId=${associationId}`
    );
    const data = await response.data;
    return data;
  }
);

export const fetchUserById = createAsyncThunk(
  "user/fetchById",
  async (id: string, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem("accessToken");
      const response = await axiosInstance.get(`${baseUrl}/users/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const changeUserPassword = createAsyncThunk(
  "user/changePassword",
  async (
    {
      id,
      currentPassword,
      newPassword,
    }: { id: string | undefined; currentPassword: string; newPassword: string },
    { rejectWithValue }
  ) => {
    try {
      const token = localStorage.getItem("accessToken");
      const response = await axiosInstance.put(
        `${baseUrl}/users/${id}/change-password`,
        { id, currentPassword, newPassword },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

const userSlice = createSlice({
  name: "user",
  initialState,
  reducers: {
    setUser(state, action) {
      state.user = action.payload;
    },
    addUser(state, action) {
      state.users.push(action.payload);
    },
    updateUser(state, action) {
      const userId = state.users.findIndex((u) => u.id === action.payload.id);
      state.users[userId] = action.payload;
    },
    deleteUser(state, action) {
      state.users = state.users.filter((u: any) => u.id !== action.payload);
    },
    setOpen(state, action) {
      state.open = action.payload;
    },
    setEditable(state) {
      state.editable = !state.editable;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchUsers.pending, (state) => {
      state.status = "loading";
    });
    builder.addCase(fetchUsers.fulfilled, (state, action) => {
      state.users = action.payload;
      state.status = "idle";
    });
    builder.addCase(fetchUsers.rejected, (state) => {
      state.status = "failed";
    });
    builder.addCase(fetchUserById.pending, (state) => {
      state.status = "loading";
    });
    builder.addCase(fetchUserById.fulfilled, (state, action) => {
      state.user = action.payload;
      state.status = "idle";
    });
    builder.addCase(fetchUserById.rejected, (state) => {
      state.status = "failed";
    });
  },
});

export function setUser(user: any) {
  return async (dispatch: Dispatch) => {
    dispatch(userSlice.actions.setUser(user));
  };
}

export const updateUser = createAsyncThunk(
  "user/updateUser",
  async (
    { id, data }: { id: any; data: Partial<User> },
    { rejectWithValue }
  ) => {
    try {
      const response = await axiosInstance.put(`/users/${id}`, data);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export function createUser(user: any, association: any) {
  return async (dispatch: Dispatch) => {
    try {
      const response = await axiosInstance.post(`/users`, {
        user,
        association,
      });
      dispatch(userSlice.actions.addUser(response.data));
    } catch (error: any) {
      throw error;
    }
  };
}

export function deleteUser(id: number) {
  return async (dispatch: Dispatch) => {
    try {
      await axiosInstance.delete(`/users/${id}`);
      dispatch(userSlice.actions.deleteUser(id));
    } catch (error: any) {
      throw error;
    }
  };
}

export function editUserForm() {
  return async (dispatch: Dispatch) => {
    dispatch(userSlice.actions.setEditable());
  };
}

export function openUserForm(open: boolean) {
  return async (dispatch: Dispatch) => {
    dispatch(userSlice.actions.setOpen(open));
  };
}

export default userSlice.reducer;
